import { useEffect, useLayoutEffect, useState } from 'react';

import {
  ClientAccountStatusType,
  OnboardingStatusType,
} from 'api/types/entity';
import { ConfirmationDialog, LoadingLayout } from 'components';
import { useTranslation } from 'libs/i18n';
import { Autocomplete, DialogProps, Input, Stack } from 'libs/ui';

import { shouldShowResultCheckInput } from './shouldShowResultCheckInput';

export interface DialogChangeStatusProps<
  OS extends OnboardingStatusType,
  StatusType extends 'onboarding' | 'account',
  Status extends StatusType extends 'onboarding' ? OS : ClientAccountStatusType
> extends Pick<DialogProps, 'isOpen' | 'onClose'> {
  statusType: StatusType;
  statuses?: Status[];
  loading?: boolean;
  submitLoading?: boolean;
  fetchStatuses: () => void;
  onChange: (status: Status, result?: string) => Promise<void>;
  currentStatus: Status | null;
}

export const DialogChangeStatus = <
  OS extends OnboardingStatusType,
  StatusType extends 'onboarding' | 'account',
  Status extends StatusType extends 'onboarding' ? OS : ClientAccountStatusType
>({
  statusType,
  statuses,
  loading,
  submitLoading,
  isOpen,
  fetchStatuses,
  onChange,
  onClose,
  currentStatus,
}: DialogChangeStatusProps<OS, StatusType, Status>) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status | null>(null);
  const [resultCheck, setResultCheck] = useState('');
  const [resultCheckError, setResultCheckError] = useState('');
  const [statusCheckError, setStatusCheckError] = useState('');
  const [showResultCheckInput, setShowResultCheckInput] = useState(false);

  const resetForm = () => {
    setStatus(null);
    setResultCheck('');
    setResultCheckError('');
    setStatusCheckError('');
  };

  const onConfirm = async () => {
    const resultHasError =
      showResultCheckInput && resultCheck.trim().length === 0;

    if (resultHasError || !status) {
      if (!status) {
        setStatusCheckError(t('validation.required'));
      }

      if (resultHasError) {
        setResultCheckError(t('validation.required'));
      }

      return;
    }
    await onChange(status, resultCheck);
  };

  const onCloseHandler = () => {
    onClose?.();
  };

  useLayoutEffect(() => {
    if (isOpen) {
      setShowResultCheckInput(shouldShowResultCheckInput(currentStatus));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useLayoutEffect(() => {
    if (isOpen) {
      fetchStatuses();
    } else {
      resetForm();
    }
  }, [fetchStatuses, isOpen]);

  // Clear errors on field changes
  useEffect(() => {
    setStatusCheckError('');
  }, [status]);

  useEffect(() => {
    setResultCheckError('');
  }, [resultCheck]);

  const title = t(`client.dialogChangeStatus.${statusType}.title`) as string;

  const subtitle =
    statusType === 'onboarding' && showResultCheckInput
      ? t('client.dialogChangeStatus.onboarding.subtitleWithResult')
      : (t(`client.dialogChangeStatus.${statusType}.subtitle`) as string);

  return (
    <ConfirmationDialog
      fullWidth
      headerCentered
      confirmButtonLabel={t('common.continue')}
      isOpen={isOpen}
      loading={submitLoading || loading}
      maxWidth="sm"
      subtitle={subtitle}
      title={title}
      onBack={onCloseHandler}
      onClose={onCloseHandler}
      onConfirm={onConfirm}
    >
      {loading || !statuses ? (
        <LoadingLayout />
      ) : (
        <Stack spacing={12}>
          {showResultCheckInput && (
            <Input
              showRequiredMark
              error={Boolean(resultCheckError)}
              helperText={resultCheckError}
              label={t('client.dialogChangeStatus.resultCheck.label')}
              placeholder={t(
                'client.dialogChangeStatus.resultCheck.placeholder'
              )}
              value={resultCheck}
              onChangeText={setResultCheck}
            />
          )}

          <Autocomplete
            helperTextAbsolute
            showRequiredMark
            error={Boolean(statusCheckError)}
            helperText={statusCheckError}
            label={t('client.dialogChangeStatus.status.label')}
            optionLabelKey="label"
            options={statuses.map((status) => ({
              label: t(`client.status.${status}`) as string,
              value: status,
            }))}
            optionValueKey="value"
            placeholder={t('client.dialogChangeStatus.status.placeholder')}
            onChange={(_, value) => setStatus(value?.value as Status)}
          />
        </Stack>
      )}
    </ConfirmationDialog>
  );
};
